import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import type { OnInit } from '@angular/core';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { OnyxChip, OnyxPhone } from '@onyx/angular';
import {
  ActionHelper,
  OnyxChipsComponent,
  OnyxDropdownDirective,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxModalComponent,
  OnyxNamePipe,
  OnyxToastService,
  OnyxTooltipDirective,
  PhoneHelper,
} from '@onyx/angular';
import { isString } from 'lodash';
import { catchError, EMPTY, map, of, Subject, switchMap, tap } from 'rxjs';
import { ModalNoteComponent } from '../../../common/components/modals/modal-note/modal-note.component';
import { UnavailabilitiesComponent } from '../../../common/components/unavailabilities/unavailabilities.component';
import { UnavailabilityIconComponent } from '../../../common/components/unavailabilities/unavailability-icon/unavailability-icon.component';
import { ModalsStore } from '../../../common/stores/modals.store';
import type { Fleet } from '../../fleet/common/interfaces/fleet';
import { FleetService } from '../../fleet/common/services/fleet.service';
import { TelematicsHelper } from '../../map/common/helpers/telematics.helper';
import { DriversRoute } from '../common/enums/drivers-route';
import {
  DRIVER_HEADER_ACTIONS,
  DriverHelper,
} from '../common/helpers/driver.helper';
import type { SimplifiedDriver } from '../common/interfaces/driver';
import { DriverStatus } from '../common/interfaces/driver-status';
import { DriversService } from '../common/services/drivers.service';
import type { Driver } from './../common/interfaces/driver';
import { DriverModalHeaderDescriptionComponent } from './driver-modal-header-description/driver-modal-header-description.component';
import { DriverModalDetailsComponent } from './driver-modal-sections/driver-modal-details/driver-modal-details.component';
import { DriverModalDocumentsComponent } from './driver-modal-sections/driver-modal-documents/driver-modal-documents.component';
import { DriverModalSummaryComponent } from './driver-modal-sections/driver-modal-summary/driver-modal-summary.component';

export interface DriverModalData {
  driver: Driver | SimplifiedDriver | string;
  size: 420 | 640;
}

enum DriverModalSection {
  SUMMARY,
  UNAVAILABILITIES,
  DETAILS,
  DOCUMENTS,
  NOTE,
}

@Component({
  selector: 'app-driver-modal',
  imports: [
    OnyxModalComponent,
    OnyxNamePipe,
    OnyxIconComponent,
    OnyxTooltipDirective,
    UnavailabilityIconComponent,
    OnyxIconButtonComponent,
    OnyxDropdownDirective,
    OnyxChipsComponent,
    UnavailabilitiesComponent,
    DriverModalDetailsComponent,
    DriverModalDocumentsComponent,
    ModalNoteComponent,
    DriverModalSummaryComponent,
    DriverModalHeaderDescriptionComponent,
  ],
  templateUrl: './driver-modal.component.html',
  styleUrl: './driver-modal.component.scss',
})
export class DriverModalComponent implements OnInit {
  protected readonly data = inject<DriverModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly driversService = inject(DriversService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(OnyxToastService);
  private readonly fleetService = inject(FleetService);
  private readonly driverHelper = inject(DriverHelper);
  private readonly modalsStore = inject(ModalsStore);

  protected readonly I18N = 'drivers.driverModal';

  protected readonly ActionHelper = ActionHelper;
  protected readonly DriverModalSection = DriverModalSection;
  protected readonly DriversRoute = DriversRoute;
  protected readonly DriverStatus = DriverStatus;

  private uuid!: string;

  protected driver = signal<Driver | null>(null);
  protected vehicle = signal<Fleet | null>(null);
  protected section = this.modalsStore.getActiveSection(
    DriverModalComponent.name,
    () => this.sections(),
  );
  protected loading = signal(false);
  protected vehicleLoading = signal(false);
  protected error = signal(false);
  protected vehicleError = signal(false);
  protected driver$ = new Subject<void>();
  protected vehicle$ = new Subject<void>();
  protected close$ = new Subject<void>();

  protected options = computed(() =>
    this.driver()
      ? this.driverHelper.getOptions(
          this.driver()!,
          { close$: this.close$ },
          DRIVER_HEADER_ACTIONS,
        )
      : null,
  );
  protected isArchived = computed(() =>
    this.driver() ? DriverHelper.isArchived(this.driver()!) : null,
  );
  protected sections = computed<OnyxChip<DriverModalSection>[]>(() => [
    ...(!this.isArchived()
      ? [{ name: 'labels.summary', value: DriverModalSection.SUMMARY }]
      : []),
    {
      name: 'labels.unavailabilities',
      value: DriverModalSection.UNAVAILABILITIES,
    },
    { name: 'labels.details', value: DriverModalSection.DETAILS },
    { name: 'labels.documents', value: DriverModalSection.DOCUMENTS },
    { name: 'labels.note', value: DriverModalSection.NOTE },
  ]);
  protected informationRowSize = computed(() =>
    this.data.size === 420 ? 120 : 184,
  );
  protected driverIcon = computed(() => {
    const driver = this.driver();
    return {
      icon: TelematicsHelper.getDriverIcon(driver),
      tooltip: driver
        ? DriverHelper.getSelectedDriverTooltip(driver, null)
        : undefined,
    };
  });

  public ngOnInit(): void {
    this.uuid = isString(this.data.driver)
      ? this.data.driver
      : this.data.driver.uuid;

    this.driver$
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.driversService.getDriver(this.uuid).pipe(
            catchError(() => {
              this.driver.set(null);
              this.loading.set(false);
              this.error.set(true);

              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((driver) => {
        this.driver.set(driver);
        this.loading.set(false);
        this.error.set(false);

        this.vehicle$.next();
      });

    this.vehicle$
      .pipe(
        map(() => this.driver()?.assignedVehicle),
        tap(() => this.vehicleLoading.set(true)),
        switchMap((vehicle) =>
          vehicle
            ? this.fleetService.getFleet(vehicle.uuid).pipe(
                catchError(() => {
                  this.vehicle.set(null);
                  this.vehicleLoading.set(false);
                  this.vehicleError.set(true);

                  return EMPTY;
                }),
              )
            : of(null),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((vehicle) => {
        this.vehicle.set(vehicle);
        this.vehicleLoading.set(false);
        this.vehicleError.set(false);
      });

    if (
      isString(this.data.driver) ||
      !DriverHelper.isDriverType(this.data.driver)
    ) {
      this.driver$.next();
    } else {
      this.driver.set(this.data.driver);
      this.vehicle$.next();
    }

    this.driversService.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.driver$.next());
  }

  protected edit(): void {
    this.driverHelper.edit(this.uuid, { close$: this.close$ });
  }

  protected share(): void {
    const driver = this.driver();
    if (driver) this.driverHelper.share(driver);
  }

  protected showOnGantt(): void {
    const driver = this.driver();
    if (driver) this.driverHelper.showOnGantt(driver, { close$: this.close$ });
  }

  protected showOnMap(): void {
    const driver = this.driver();
    if (driver) this.driverHelper.showOnMap(driver, { close$: this.close$ });
  }

  protected copyPhone(phone: OnyxPhone) {
    ActionHelper.copy(PhoneHelper.composeLabel(phone), this.toastService);
  }

  protected copy(value: string) {
    ActionHelper.copy(value, this.toastService);
  }
}
